import React, { useState, useEffect, useCallback } from 'react';
import { FaShieldAlt, FaUserMd, FaUserInjured, FaUsers, FaChartBar, FaSearch, FaEdit, FaTrash, FaSignInAlt, FaCommentDots, FaLink, FaRupeeSign, FaClipboardCheck, FaCheck, FaTimes, FaBan, FaUserSlash, FaCalendarTimes, FaPlus } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp } from '../context/AppContext';
import { adminGetUsers, adminUpdateUser, adminDeleteUser, adminImpersonate, adminGetAnalytics, adminGetChats, adminGetDoctorPatientMap, adminAssignDoctor, getDoctors, adminGetPaymentSummary, getPendingApprovalBillings, approveBilling, rejectBilling, getFileUrl, blockConversation, disableConversation, getDoctorHolidays, createDoctorHoliday, updateDoctorHoliday, deleteDoctorHoliday } from '../services/api';

const AdminPanel = () => {
  const { showToast } = useApp();
  const [tab, setTab] = useState('analytics');
  const [users, setUsers] = useState([]);
  const [chats, setChats] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [doctorPatientMap, setDoctorPatientMap] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [assignPatient, setAssignPatient] = useState(null);
  const [assignDoctorId, setAssignDoctorId] = useState('');

  const [viewUser, setViewUser] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentTotalRevenue, setPaymentTotalRevenue] = useState(0);
  const [paymentDoctorFilter, setPaymentDoctorFilter] = useState('');
  const [billingApprovals, setBillingApprovals] = useState([]);
  const [approvalActionId, setApprovalActionId] = useState(null);

  const [holidayDoctorId, setHolidayDoctorId] = useState('');
  const [holidays, setHolidays] = useState([]);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({ date: '', type: 'one_time', reason: '', status: 'active' });
  const [savingHoliday, setSavingHoliday] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminGetAnalytics();
      setAnalytics(res.data?.data || null);
    } catch { setAnalytics(null); }
    setLoading(false);
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminGetUsers({ role: roleFilter || undefined, search: search || undefined });
      setUsers(res.data?.data || []);
    } catch { setUsers([]); }
    setLoading(false);
  }, [roleFilter, search]);

  const fetchChats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminGetChats();
      setChats(res.data?.data || []);
    } catch { setChats([]); }
    setLoading(false);
  }, []);

  const handleChatToggleBlock = async (chat) => {
    const willBlock = !chat.blockedBy || chat.blockedBy === 'none';
    if (willBlock && !window.confirm(`Block messaging between ${chat.patientName} and Dr. ${chat.doctorName}?`)) return;
    try {
      const res = await blockConversation(chat.id, willBlock ? 'block' : 'unblock');
      setChats(prev => prev.map(c => c.id === chat.id ? { ...c, ...res.data.data } : c));
      showToast(willBlock ? 'Conversation blocked' : 'Conversation unblocked', 'success');
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const handleChatToggleDisable = async (chat) => {
    const willDisable = !chat.chatDisabled;
    if (willDisable && !window.confirm(`Disable chat between ${chat.patientName} and Dr. ${chat.doctorName}?`)) return;
    try {
      const res = await disableConversation(chat.id, willDisable);
      setChats(prev => prev.map(c => c.id === chat.id ? { ...c, ...res.data.data } : c));
      showToast(willDisable ? 'Chat disabled' : 'Chat re-enabled', 'success');
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const fetchPayments = useCallback(async (doctorId) => {
    setLoading(true);
    try {
      const [payRes, docRes] = await Promise.allSettled([
        adminGetPaymentSummary(doctorId ? { doctorId } : {}),
        getDoctors(),
      ]);
      if (payRes.status === 'fulfilled') {
        setPayments(payRes.value.data?.data || []);
        setPaymentTotalRevenue(payRes.value.data?.totalRevenue || 0);
      }
      if (docRes.status === 'fulfilled') setAllDoctors(docRes.value.data?.data || []);
    } catch { setPayments([]); }
    setLoading(false);
  }, []);

  const fetchBillingApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const [billRes, docRes] = await Promise.allSettled([getPendingApprovalBillings(), getDoctors()]);
      setBillingApprovals(billRes.status === 'fulfilled' ? (billRes.value.data?.data || []) : []);
      if (docRes.status === 'fulfilled') setAllDoctors(docRes.value.data?.data || []);
    } catch { setBillingApprovals([]); }
    setLoading(false);
  }, []);

  const fetchDoctorPatientMap = useCallback(async () => {
    setLoading(true);
    try {
      const [mapRes, docRes] = await Promise.allSettled([adminGetDoctorPatientMap(), getDoctors()]);
      if (mapRes.status === 'fulfilled') setDoctorPatientMap(mapRes.value.data?.data || []);
      if (docRes.status === 'fulfilled') setAllDoctors(docRes.value.data?.data || []);
    } catch { setDoctorPatientMap([]); }
    setLoading(false);
  }, []);

  const fetchHolidays = useCallback(async (doctorId) => {
    setLoading(true);
    try {
      const [holRes, docRes] = await Promise.allSettled([
        doctorId ? getDoctorHolidays(doctorId) : Promise.resolve({ data: { data: [] } }),
        getDoctors(),
      ]);
      setHolidays(holRes.status === 'fulfilled' ? (holRes.value.data?.data || []) : []);
      if (docRes.status === 'fulfilled') setAllDoctors(docRes.value.data?.data || []);
    } catch { setHolidays([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === 'analytics') fetchAnalytics();
    else if (tab === 'users') fetchUsers();
    else if (tab === 'chats') fetchChats();
    else if (tab === 'assign') fetchDoctorPatientMap();
    else if (tab === 'payments') fetchPayments(paymentDoctorFilter || undefined);
    else if (tab === 'billingApprovals') fetchBillingApprovals();
    else if (tab === 'holidays') fetchHolidays(holidayDoctorId || undefined);
  }, [tab, fetchAnalytics, fetchUsers, fetchChats, fetchDoctorPatientMap, fetchPayments, paymentDoctorFilter, fetchBillingApprovals, fetchHolidays, holidayDoctorId]);

  const handleApproveBilling = async (id) => {
    setApprovalActionId(id);
    try {
      await approveBilling(id);
      setBillingApprovals(prev => prev.filter(b => b.id !== id));
      showToast('Invoice approved — patient notified.', 'success');
    } catch (err) { showToast(err.message || 'Approve failed', 'error'); }
    setApprovalActionId(null);
  };

  const handleRejectBilling = async (id) => {
    if (!window.confirm('Reject this invoice request? The doctor will be notified.')) return;
    setApprovalActionId(id);
    try {
      await rejectBilling(id);
      setBillingApprovals(prev => prev.filter(b => b.id !== id));
      showToast('Invoice rejected.', 'success');
    } catch (err) { showToast(err.message || 'Reject failed', 'error'); }
    setApprovalActionId(null);
  };

  const handleAssignDoctor = async () => {
    if (!assignPatient || !assignDoctorId) return;
    setSaving(true);
    try {
      await adminAssignDoctor({ patientId: assignPatient.id, doctorId: assignDoctorId });
      showToast('Doctor assigned successfully!', 'success');
      setAssignPatient(null); setAssignDoctorId('');
      fetchDoctorPatientMap();
    } catch (err) { showToast(err.message || 'Failed to assign', 'error'); }
    setSaving(false);
  };

  const handleImpersonate = async (userId, name) => {
    if (!window.confirm(`Login as ${name}? This will switch your session.`)) return;
    try {
      const res = await adminImpersonate(userId);
      const { token, user: userData } = res.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      window.location.href = '/';
    } catch (err) { showToast(err.message || 'Impersonation failed', 'error'); }
  };

  const handleToggleCanCreateInvoice = async (u) => {
    const next = u.canCreateInvoice === false; // toggling to true if currently disabled
    try {
      await adminUpdateUser(u.id, { canCreateInvoice: next });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, canCreateInvoice: next } : x));
      showToast(next ? 'Doctor can create invoices again.' : 'Doctor blocked from creating invoices.', 'success');
    } catch (err) { showToast(err.message || 'Update failed', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await adminDeleteUser(id);
      setUsers(prev => prev.filter(u => u.id !== id));
      showToast('User deactivated');
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const handleEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name, email: u.email, role: u.role, status: u.status, phone: u.phone || '', specialization: u.specialization || '' });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await adminUpdateUser(editUser.id, editForm);
      setUsers(prev => prev.map(u => u.id === editUser.id ? res.data?.data : u));
      showToast('User updated!', 'success');
      setEditUser(null);
    } catch (err) { showToast(err.message || 'Update failed', 'error'); }
    setSaving(false);
  };

  const openAddHoliday = () => {
    setEditingHoliday(null);
    setHolidayForm({ date: '', type: 'one_time', reason: '', status: 'active' });
    setShowHolidayModal(true);
  };

  const openEditHoliday = (h) => {
    setEditingHoliday(h);
    setHolidayForm({ date: h.date, type: h.type, reason: h.reason || '', status: h.status });
    setShowHolidayModal(true);
  };

  const handleSaveHoliday = async () => {
    if (!holidayForm.date) { showToast('Select a holiday date', 'error'); return; }
    setSavingHoliday(true);
    try {
      if (editingHoliday) {
        await updateDoctorHoliday(editingHoliday.id, holidayForm);
        showToast('Holiday updated!', 'success');
      } else if (holidayDoctorId === 'all') {
        await createDoctorHoliday({ applyToAll: true, ...holidayForm });
        showToast('Global holiday added — applies to every doctor!', 'success');
      } else {
        await createDoctorHoliday({ doctorId: holidayDoctorId, ...holidayForm });
        showToast('Holiday added!', 'success');
      }
      setShowHolidayModal(false);
      fetchHolidays(holidayDoctorId);
    } catch (err) { showToast(err.message || 'Save failed', 'error'); }
    setSavingHoliday(false);
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await deleteDoctorHoliday(id);
      setHolidays(prev => prev.filter(h => h.id !== id));
      showToast('Holiday deleted', 'success');
    } catch (err) { showToast(err.message || 'Delete failed', 'error'); }
  };

  const handleToggleHolidayStatus = async (h) => {
    const next = h.status === 'active' ? 'inactive' : 'active';
    try {
      await updateDoctorHoliday(h.id, { status: next });
      setHolidays(prev => prev.map(x => x.id === h.id ? { ...x, status: next } : x));
      showToast(next === 'active' ? 'Holiday enabled' : 'Holiday disabled', 'success');
    } catch (err) { showToast(err.message || 'Update failed', 'error'); }
  };

  // Exempts a specific doctor from the automatic Saturday/Sunday block —
  // does not affect doctor_holidays (those still block regardless).
  const handleToggleWorksWeekends = async (doc) => {
    const next = !doc.worksWeekends;
    try {
      await adminUpdateUser(doc.id, { worksWeekends: next });
      setAllDoctors(prev => prev.map(d => d.id === doc.id ? { ...d, worksWeekends: next } : d));
      showToast(next ? 'Doctor can now work Saturday/Sunday' : 'Weekend work disabled for this doctor', 'success');
    } catch (err) { showToast(err.message || 'Update failed', 'error'); }
  };

  // Annual holidays show without a year (they recur every year); one-time
  // holidays show the exact year they block.
  const formatHolidayDate = (h) => {
    const [y, m, d] = h.date.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const day = dt.getUTCDate();
    const mon = dt.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
    return h.type === 'annual' ? `${day} ${mon}` : `${day} ${mon} ${y}`;
  };

  const tabs = [
    { key: 'analytics',        label: 'Analytics',         icon: FaChartBar },
    { key: 'users',            label: 'Users',             icon: FaUsers },
    { key: 'payments',         label: 'Payments',          icon: FaRupeeSign },
    { key: 'billingApprovals', label: 'Billing Approvals', icon: FaClipboardCheck },
    { key: 'assign',           label: 'Assign Doctor',      icon: FaLink },
    { key: 'chats',            label: 'Chat Monitor',       icon: FaCommentDots },
    { key: 'holidays',         label: 'Holiday Management', icon: FaCalendarTimes },
  ];

  const StatCard = ({ label, value, sub, color }) => (
    <div className="card" style={{ padding: 16, textAlign: 'center', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dark)', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div className="page-wrapper">
      <Header title="Admin Panel" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div><h1><FaShieldAlt style={{ color: 'var(--primary)', marginRight: 8 }} />Admin Panel</h1><p>Full system control</p></div>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, overflowX: 'auto' }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', background: tab === t.key ? 'var(--primary)' : 'white', color: tab === t.key ? 'white' : 'var(--text-medium)', boxShadow: 'var(--shadow)' }}>
              <t.icon /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : tab === 'analytics' && analytics ? (
          <div>
            <div className="section-title">System Overview</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 16 }}>
              <StatCard label="Doctors" value={analytics.users?.doctors || 0} color="#1976d2" />
              <StatCard label="Patients" value={analytics.users?.patients || 0} color="#2e7d32" />
              <StatCard label="Total Appointments" value={analytics.appointments?.total || 0} sub={`Today: ${analytics.appointments?.today || 0}`} color="#E8500A" />
              <StatCard label="Completed" value={analytics.appointments?.completed || 0} sub={`Month: ${analytics.appointments?.month || 0}`} color="#7c3aed" />
            </div>
            <div className="section-title">Billing & Revenue</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 16 }}>
              <StatCard label="Appointment Payments" value={`₹${(analytics.payments?.revenue || 0).toLocaleString('en-IN')}`} sub={`${analytics.payments?.count || 0} paid bookings`} color="#E8500A" />
              <StatCard label="Billing Revenue" value={`₹${(analytics.billing?.revenue || 0).toLocaleString('en-IN')}`} sub={`${analytics.billing?.paid || 0} paid bills`} color="#2e7d32" />
              <StatCard label="Total Revenue" value={`₹${((analytics.payments?.revenue || 0) + (analytics.billing?.revenue || 0)).toLocaleString('en-IN')}`} color="#7c3aed" />
              <StatCard label="Pending Bills" value={analytics.billing?.pending || 0} color="#e65100" />
            </div>
            <div className="section-title">Telemedicine</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
              <StatCard label="Total Sessions" value={analytics.telemedicine?.total || 0} color="#1976d2" />
              <StatCard label="Scheduled" value={analytics.telemedicine?.active || 0} color="#E8500A" />
            </div>
          </div>
        ) : tab === 'users' ? (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <div className="search-bar" style={{ flex: 1, marginBottom: 0 }}>
                <FaSearch /><input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ padding: '10px 12px', border: '2px solid var(--border)', borderRadius: 10, fontSize: 13, background: 'white' }}>
                <option value="">All Roles</option>
                <option value="Admin">Admin</option>
                <option value="Doctor">Doctor</option>
                <option value="Patient">Patient</option>
              </select>
            </div>
            {users.length === 0 ? (
              <div className="empty-state"><FaUsers className="empty-state-icon" /><h3>No users found</h3></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {users.map(u => {
                  const roleColor = u.role === 'Admin' ? '#7c3aed' : u.role === 'Doctor' ? '#1976d2' : '#2e7d32';
                  const initials = u.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                  return (
                    <div key={u.id} className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 44, height: 44, borderRadius: '50%', background: roleColor + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', color: roleColor, fontWeight: 800, fontSize: 15, flexShrink: 0 }}>{initials}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{u.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-light)' }}>{u.email}</div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: roleColor + '22', color: roleColor }}>{u.role}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: u.status === 'active' ? '#e8f5e9' : '#ffebee', color: u.status === 'active' ? '#2e7d32' : '#c62828' }}>{u.status}</span>
                          {u.role === 'Doctor' && u.canCreateInvoice === false && (
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#ffebee', color: '#c62828' }}>Invoice Creation Disabled</span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="action-btn" style={{ background: '#e0f2fe', color: '#0284c7' }} onClick={() => setViewUser(u)} title="View Details"><FaSearch /></button>
                        <button className="action-btn action-btn-edit" onClick={() => handleEdit(u)} title="Edit"><FaEdit /></button>
                        {u.role === 'Doctor' && (
                          <button
                            className="action-btn"
                            style={u.canCreateInvoice === false ? { background: '#e8f5e9', color: '#2e7d32' } : { background: '#fff3e0', color: '#e65100' }}
                            onClick={() => handleToggleCanCreateInvoice(u)}
                            title={u.canCreateInvoice === false ? 'Allow this doctor to create invoices' : 'Block this doctor from creating invoices'}
                          >
                            {u.canCreateInvoice === false ? <FaCheck /> : <FaBan />}
                          </button>
                        )}
                        <button className="action-btn" style={{ background: '#ede9fe', color: '#7c3aed' }} onClick={() => handleImpersonate(u.id, u.name)} title="Login as this user"><FaSignInAlt /></button>
                        <button className="action-btn action-btn-delete" onClick={() => handleDelete(u.id)} title="Deactivate"><FaTrash /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : tab === 'payments' ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div className="section-title" style={{ marginBottom: 2 }}>Appointment Payments</div>
                <div style={{ fontSize: 13, color: 'var(--text-light)' }}>
                  Total: <strong style={{ color: '#E8500A' }}>₹{paymentTotalRevenue.toLocaleString('en-IN')}</strong>
                  &nbsp;({payments.length} transactions)
                </div>
              </div>
              <select
                value={paymentDoctorFilter}
                onChange={e => setPaymentDoctorFilter(e.target.value)}
                style={{ padding: '10px 12px', border: '2px solid var(--border)', borderRadius: 10, fontSize: 13, background: 'white', minWidth: 180 }}
              >
                <option value="">All Doctors</option>
                {allDoctors.map(d => (
                  <option key={d.id} value={d.id}>Dr. {d.name}{d.specialization ? ` (${d.specialization})` : ''}</option>
                ))}
              </select>
            </div>
            {payments.length === 0 ? (
              <div className="empty-state"><FaRupeeSign className="empty-state-icon" /><h3>No payments found</h3></div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>Patient</th><th>Doctor</th><th>Date</th><th>Mode</th><th>Amount</th><th>Payment ID</th></tr>
                    </thead>
                    <tbody>
                      {payments.map(p => (
                        <tr key={p.id}>
                          <td><strong>{p.patientName}</strong></td>
                          <td style={{ color: '#1976d2', fontWeight: 600 }}>{p.doctorName !== '-' ? `Dr. ${p.doctorName}` : '-'}</td>
                          <td>{p.date !== '-' ? new Date(p.date + 'T00:00:00').toLocaleDateString('en-IN') : new Date(p.createdAt).toLocaleDateString('en-IN')}</td>
                          <td>{p.mode}</td>
                          <td><strong style={{ color: '#2e7d32' }}>₹{p.amount.toLocaleString('en-IN')}</strong></td>
                          <td style={{ fontSize: 11, color: 'var(--text-light)', fontFamily: 'monospace' }}>{p.paymentId || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : tab === 'billingApprovals' ? (
          <div>
            <div className="section-title">Doctor Invoice Requests</div>
            <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 14 }}>
              Invoices created by doctors wait here until you approve them — the patient is not notified or able to pay until then.
            </p>
            {billingApprovals.length === 0 ? (
              <div className="empty-state"><FaClipboardCheck className="empty-state-icon" /><h3>No invoices awaiting approval</h3></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {billingApprovals.map(b => {
                  const svcs = b.services || b.BillingServices || [];
                  return (
                    <div key={b.id} className="card" style={{ padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15 }}>{b.patientName}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 2 }}>
                            Requested by Dr. {allDoctors.find(d => d.id === b.doctorId)?.name || `#${b.doctorId}`}
                            {' · '}{b.date ? new Date(b.date).toLocaleDateString('en-IN') : '-'}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {svcs.map((s, i) => (
                              <span key={i} style={{ fontSize: 12, background: '#f4f5f7', padding: '3px 9px', borderRadius: 8 }}>
                                {s.name} — ₹{s.amount}
                              </span>
                            ))}
                          </div>
                          {b.notes && <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 6, fontStyle: 'italic' }}>"{b.notes}"</div>}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--primary)', marginBottom: 8 }}>
                            ₹{(parseFloat(b.totalAmount) || 0).toLocaleString()}
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn btn-primary" style={{ padding: '7px 14px', fontSize: 12 }}
                              disabled={approvalActionId === b.id}
                              onClick={() => handleApproveBilling(b.id)}
                            >
                              <FaCheck style={{ marginRight: 5 }} />Approve
                            </button>
                            <button
                              className="btn btn-danger" style={{ padding: '7px 14px', fontSize: 12 }}
                              disabled={approvalActionId === b.id}
                              onClick={() => handleRejectBilling(b.id)}
                            >
                              <FaTimes style={{ marginRight: 5 }} />Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : tab === 'assign' ? (
          <div>
            <div className="section-title">Doctor–Patient Assignments</div>
            <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 14 }}>
              View all patients and their assigned doctor. Click "Assign" to change or set a doctor.
            </p>
            {doctorPatientMap.length === 0 ? (
              <div className="empty-state"><FaUserInjured className="empty-state-icon" /><h3>No patients found</h3></div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>Patient</th><th>Phone</th><th>Assigned Doctor</th><th>Action</th></tr>
                    </thead>
                    <tbody>
                      {doctorPatientMap.map(p => (
                        <tr key={p.id}>
                          <td><strong>{p.name}</strong></td>
                          <td>{p.phone || '-'}</td>
                          <td>
                            {p.doctor
                              ? <span style={{ color: '#1976d2', fontWeight: 600 }}>Dr. {p.doctor.name}{p.doctor.specialization ? ` (${p.doctor.specialization})` : ''}</span>
                              : <span style={{ color: '#e65100', fontStyle: 'italic' }}>Unassigned</span>
                            }
                          </td>
                          <td>
                            <button className="action-btn action-btn-edit" onClick={() => { setAssignPatient(p); setAssignDoctorId(p.doctorId || ''); }} title="Assign/Change Doctor">
                              <FaUserMd />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : tab === 'chats' ? (
          <div>
            <div className="section-title">All Chat Requests</div>
            {chats.length === 0 ? (
              <div className="empty-state"><FaCommentDots className="empty-state-icon" /><h3>No chat requests</h3></div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="data-table">
                  <thead><tr><th>Patient</th><th>Doctor</th><th>Status</th><th>Date</th><th>Message</th><th>Actions</th></tr></thead>
                  <tbody>
                    {chats.map(c => (
                      <tr key={c.id}>
                        <td><strong>{c.patientName || '-'}</strong></td>
                        <td>{c.doctorName || '-'}</td>
                        <td>
                          <span className={`status-badge status-${c.status}`}>{c.status}</span>
                          {c.blockedBy && c.blockedBy !== 'none' && <span style={{ marginLeft: 6, fontSize: 10, background: '#ffebee', color: '#c62828', padding: '2px 6px', borderRadius: 6 }}>BLOCKED</span>}
                          {c.chatDisabled && <span style={{ marginLeft: 6, fontSize: 10, background: '#fff3e0', color: '#e65100', padding: '2px 6px', borderRadius: 6 }}>DISABLED</span>}
                        </td>
                        <td>{c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-IN') : '-'}</td>
                        <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.message || '-'}</td>
                        <td>
                          {c.status === 'accepted' ? (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="action-btn" style={{ background: c.chatDisabled ? '#e8f5e9' : '#fff3e0', color: c.chatDisabled ? '#2e7d32' : '#e65100' }} onClick={() => handleChatToggleDisable(c)} title={c.chatDisabled ? 'Enable Chat' : 'Disable Chat'}><FaBan /></button>
                              <button className="action-btn" style={{ background: (c.blockedBy && c.blockedBy !== 'none') ? '#e8f5e9' : '#ffebee', color: (c.blockedBy && c.blockedBy !== 'none') ? '#2e7d32' : '#c62828' }} onClick={() => handleChatToggleBlock(c)} title={(c.blockedBy && c.blockedBy !== 'none') ? 'Unblock' : 'Block'}><FaUserSlash /></button>
                            </div>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : tab === 'holidays' ? (
          <div>
            <div className="section-title">Holiday Management</div>
            <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 14 }}>
              Saturday and Sunday are unavailable automatically for every doctor, unless exempted below. Add one-time or yearly-recurring holidays per doctor, or pick "All Doctors" for a holiday that applies to everyone (including doctors added later).
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
              <select
                value={holidayDoctorId}
                onChange={e => setHolidayDoctorId(e.target.value)}
                style={{ padding: '10px 12px', border: '2px solid var(--border)', borderRadius: 10, fontSize: 13, background: 'white', minWidth: 220 }}
              >
                <option value="">Select Doctor</option>
                <option value="all">🌐 All Doctors (Global)</option>
                {allDoctors.map(d => (
                  <option key={d.id} value={d.id}>Dr. {d.name}{d.specialization ? ` (${d.specialization})` : ''}</option>
                ))}
              </select>
              <button className="btn btn-primary" disabled={!holidayDoctorId} onClick={openAddHoliday}>
                <FaPlus style={{ marginRight: 5 }} />Add Holiday
              </button>
            </div>

            {holidayDoctorId && holidayDoctorId !== 'all' && (() => {
              const doc = allDoctors.find(d => String(d.id) === String(holidayDoctorId));
              if (!doc) return null;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', background: 'var(--primary-bg)', borderRadius: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Works on Saturday/Sunday:</span>
                  <button
                    className="action-btn"
                    style={doc.worksWeekends ? { background: '#e8f5e9', color: '#2e7d32' } : { background: '#ffebee', color: '#c62828' }}
                    onClick={() => handleToggleWorksWeekends(doc)}
                  >
                    {doc.worksWeekends ? <FaCheck style={{ marginRight: 5 }} /> : <FaBan style={{ marginRight: 5 }} />}
                    {doc.worksWeekends ? 'Allowed' : 'Not Allowed'}
                  </button>
                </div>
              );
            })()}

            {!holidayDoctorId ? (
              <div className="empty-state"><FaCalendarTimes className="empty-state-icon" /><h3>Select a doctor to manage their holidays</h3></div>
            ) : holidays.length === 0 ? (
              <div className="empty-state"><FaCalendarTimes className="empty-state-icon" /><h3>{holidayDoctorId === 'all' ? 'No global holidays added yet' : 'No holidays added for this doctor'}</h3></div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>Date</th><th>Type</th><th>Reason</th><th>Scope</th><th>Status</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {holidays.map(h => (
                        <tr key={h.id}>
                          <td><strong>{formatHolidayDate(h)}</strong></td>
                          <td>{h.type === 'annual' ? 'Every Year' : 'One-time'}</td>
                          <td>{h.reason || '-'}</td>
                          <td>{h.doctorId == null ? <span style={{ color: '#7c3aed', fontWeight: 600 }}>All Doctors</span> : 'This Doctor'}</td>
                          <td>
                            <span className={`status-badge status-${h.status === 'active' ? 'scheduled' : 'cancelled'}`}>{h.status}</span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="action-btn action-btn-edit" onClick={() => openEditHoliday(h)} title="Edit"><FaEdit /></button>
                              <button
                                className="action-btn"
                                style={h.status === 'active' ? { background: '#fff3e0', color: '#e65100' } : { background: '#e8f5e9', color: '#2e7d32' }}
                                onClick={() => handleToggleHolidayStatus(h)}
                                title={h.status === 'active' ? 'Disable holiday' : 'Enable holiday'}
                              >
                                {h.status === 'active' ? <FaBan /> : <FaCheck />}
                              </button>
                              <button className="action-btn action-btn-delete" onClick={() => handleDeleteHoliday(h.id)} title="Delete"><FaTrash /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </main>
      <BottomNav />

      {assignPatient && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAssignPatient(null)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2>Assign Doctor — {assignPatient.name}</h2>
              <button className="modal-close" onClick={() => setAssignPatient(null)}>×</button>
            </div>
            <div className="form-group">
              <label>Select Doctor *</label>
              <select value={assignDoctorId} onChange={e => setAssignDoctorId(e.target.value)}>
                <option value="">Choose a doctor...</option>
                {allDoctors.map(d => (
                  <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ''}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setAssignPatient(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleAssignDoctor} disabled={saving || !assignDoctorId}>
                {saving ? 'Assigning...' : 'Assign Doctor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showHolidayModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowHolidayModal(false)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2>
                {editingHoliday ? 'Edit Holiday' : 'Add Holiday'} — {
                  editingHoliday
                    ? (editingHoliday.doctorId == null ? 'All Doctors' : (allDoctors.find(d => String(d.id) === String(editingHoliday.doctorId))?.name ? `Dr. ${allDoctors.find(d => String(d.id) === String(editingHoliday.doctorId)).name}` : 'This Doctor'))
                    : (holidayDoctorId === 'all' ? 'All Doctors' : (allDoctors.find(d => String(d.id) === String(holidayDoctorId))?.name ? `Dr. ${allDoctors.find(d => String(d.id) === String(holidayDoctorId)).name}` : ''))
                }
              </h2>
              <button className="modal-close" onClick={() => setShowHolidayModal(false)}>×</button>
            </div>
            <div className="form-group">
              <label>Holiday Date *</label>
              <input type="date" value={holidayForm.date} onChange={e => setHolidayForm(p => ({ ...p, date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Holiday Type *</label>
              <select value={holidayForm.type} onChange={e => setHolidayForm(p => ({ ...p, type: e.target.value }))}>
                <option value="one_time">One-time</option>
                <option value="annual">Every Year</option>
              </select>
            </div>
            <div className="form-group">
              <label>Reason</label>
              <input type="text" value={holidayForm.reason} onChange={e => setHolidayForm(p => ({ ...p, reason: e.target.value }))} placeholder="e.g. Christmas" />
            </div>
            {editingHoliday && (
              <div className="form-group">
                <label>Status</label>
                <select value={holidayForm.status} onChange={e => setHolidayForm(p => ({ ...p, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowHolidayModal(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSaveHoliday} disabled={savingHoliday || !holidayForm.date}>
                {savingHoliday ? 'Saving...' : 'Save Holiday'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editUser && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditUser(null)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2>Edit User — {editUser.name}</h2>
              <button className="modal-close" onClick={() => setEditUser(null)}>×</button>
            </div>
            {[
              { f: 'name', l: 'Name' }, { f: 'email', l: 'Email', t: 'email' },
              { f: 'phone', l: 'Phone' }, { f: 'specialization', l: 'Specialization' },
            ].map(({ f, l, t }) => (
              <div className="form-group" key={f}>
                <label>{l}</label>
                <input type={t || 'text'} value={editForm[f] || ''} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value }))} />
              </div>
            ))}
            <div className="form-group">
              <label>Set New Password (Leave blank to keep current)</label>
              <input type="password" value={editForm.password || ''} onChange={e => setEditForm(p => ({ ...p, password: e.target.value }))} placeholder="Enter new password" />
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Role</label>
                <select value={editForm.role} onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))}>
                  <option value="Admin">Admin</option>
                  <option value="Doctor">Doctor</option>
                  <option value="Patient">Patient</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={editForm.status} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setEditUser(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      {viewUser && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewUser(null)}>
          <div className="modal-box" style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {viewUser.profileImage ? (
                  <img src={getFileUrl(viewUser.profileImage)} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--primary-bg)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                    {viewUser.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                )}
                <h2 style={{ margin: 0 }}>User Details — {viewUser.name}</h2>
              </div>
              <button className="modal-close" onClick={() => setViewUser(null)}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, fontSize: 14, color: 'var(--text-dark)', maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Role</strong><div>{viewUser.role}</div></div>
              <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Status</strong><div>{viewUser.status}</div></div>
              <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Email</strong><div>{viewUser.email}</div></div>
              <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Phone</strong><div>{viewUser.phone || '-'}</div></div>
              <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Age</strong><div>{viewUser.age || '-'}</div></div>
              <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Gender</strong><div>{viewUser.gender || '-'}</div></div>
              <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Blood Group</strong><div>{viewUser.bloodGroup || '-'}</div></div>
              {viewUser.role === 'Doctor' && <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Specialization</strong><div>{viewUser.specialization || '-'}</div></div>}
              {viewUser.role === 'Doctor' && <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Registration No.</strong><div>{viewUser.regNo || '-'}</div></div>}
              {viewUser.role === 'Doctor' && <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Experience</strong><div>{viewUser.experience || '-'}</div></div>}
              {viewUser.role === 'Doctor' && <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Consultation Fee</strong><div>{viewUser.consultFee ? `₹${viewUser.consultFee}` : '-'}</div></div>}
              {viewUser.role === 'Doctor' && <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Timing</strong><div>{viewUser.timing || '-'}</div></div>}
              {viewUser.role === 'Doctor' && <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Degree</strong><div>{viewUser.degree || '-'}</div></div>}
              {(viewUser.role === 'Doctor' || viewUser.role === 'Patient') && <div><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Hospital</strong><div>{viewUser.hospital || '-'}</div></div>}
              <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Address</strong><div>{viewUser.address || '-'}</div></div>
              <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>About</strong><div>{viewUser.about || '-'}</div></div>
              {(viewUser.role === 'Patient' || viewUser.role === 'Admin') && <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Medical History</strong><div>{viewUser.medicalHistory || '-'}</div></div>}
              {(viewUser.role === 'Patient' || viewUser.role === 'Admin') && <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Allergies</strong><div>{viewUser.allergies || '-'}</div></div>}
              <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: 'var(--text-light)', fontSize: 11, textTransform: 'uppercase' }}>Created At</strong><div>{new Date(viewUser.createdAt).toLocaleString('en-IN')}</div></div>
            </div>
            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <button className="btn btn-primary" onClick={() => setViewUser(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminPanel;
